<template>
  <div
    class="flex cursor-pointer items-center -ml-0.5"
    :class="[[disabled ? 'opacity-50 line-through' : ''],[textClass]]"
  >
    <input
      type="radio"
      :checked="isChecked"
      :name="name"
      :disabled="readonly || disabled"
      :style="styles"
      tabindex="0"
      class="custom-input appearance-none outline-none border-none focus:ring-2 ring-offset-2"
      @change="updateModelValue"
    />
    <button
      v-if="showOptionText"
      type="button"
      :id="`${name}-${index || 0}`"
      class="flex select-none items-center gap-2"
      @click="updateModelValue">
      <img
        v-if="option?.icon"
        :src="option?.icon"
        class="h-5 w-5"
        alt="Option Icon"
      />
      <slot>{{ optionName }}</slot>
    </button>
  </div>
</template>

<script setup>
import {computed} from "vue";
import {addOpacityToHex} from "../helpers/Color";

const emits = defineEmits(["update:modelValue"]);
const props = defineProps({
  modelValue: [String, Number, Boolean, Date],
  option: [String, Number, Boolean, Object],
  disabled: Boolean,
  name: String,
  index: Number,
  showOptionText: {
    type: Boolean,
    default: true,
  },
  customAccentHex: String,
  textClass: {
    type: String,
    default: "text-sm text-slate-500 gap-4",
  },
  readonly: Boolean,
});

const updateModelValue = () => {
  if (props.readonly || props.disabled) return;

  const value = typeof props.option === "object" ? props.option?.value : props.option;

  if (props.modelValue === value) return;
  emits("update:modelValue", value);
};

const optionName = computed(() => {
  return typeof props.option === "object" ? props.option?.name : props.option;
});

const optionValue = computed(() => {
  return typeof props.option === "object" && props.option?.value ? props.option?.value : props.option;
});

const isChecked = computed(() => {
  const digitsOnly = /^\d+$/;
  const isInt = digitsOnly.test(props.modelValue);
  if (isInt && parseInt(props.modelValue) === optionValue.value) return true;
  return props.modelValue === optionValue.value;
});

const styles = computed(() => {
  return {
    "accent-color": props.customAccentHex || "#2c5db6",
    "--accent-color": props.customAccentHex || "#2c5db6",
    "border-color": addOpacityToHex(props.customAccentHex || "#2c5db6", 0.5),
    "--hover-background-color": addOpacityToHex(props.customAccentHex || "#2c5db6", 0.05),
    "--tw-ring-color": addOpacityToHex(props.customAccentHex || "#2c5db6", 0.25),
  };
});
</script>

<style scoped>
.custom-input {
  position: relative;
  transition: background-color 0.2s;
  height: 1.375rem;
  width: 1.375rem;
  border-radius: 50%;
}

.custom-input::before {
  content: "";
  position: absolute;
  inset: 0;
  background-color: transparent;
  border-radius: 50%;
  transition: background-color 0.2s;
  pointer-events: none;
  border: 1px solid #cbd5e1;
}

.custom-input:hover::before,
input[type="radio"]:checked::before {
  background-color: var(--hover-background-color);
  border: 1px solid var(--accent-color);
  outline: none;
}

input[type="radio"]:checked::after {
  content: "";
  background-color: var(--accent-color);
  color: transparent;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  height: 60%;
  width: 60%;
  transform: translate(-50%, -50%);
}
</style>
